import { useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, EffectCoverflow } from "swiper/modules";
import { motion } from "framer-motion";
import { googleReviewsData } from "../data/googleReviews";
import SectionHeader from "./SectionHeader";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa6";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-coverflow";

const GoogleReviews = () => {
	const { businessName, overallRating, totalReviews, reviews } =
		googleReviewsData;

	const renderStars = (rating) => {
		return (
			<div className="flex gap-1">
				{[...Array(5)].map((_, i) => (
					<svg
						key={i}
						className={`w-4 h-4 ${
							i < rating ? "text-steerYellow" : "text-gray-300"
						}`}
						fill="currentColor"
						viewBox="0 0 20 20"
					>
						<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
					</svg>
				))}
			</div>
		);
	};

	const getInitials = (name) => {
		return name
			.split(" ")
			.map((word) => word[0])
			.join("")
			.toUpperCase()
			.slice(0, 2);
	};

	const ReviewCard = ({ review }) => {
		return (
			<div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-xl flex flex-col h-full">
				{/* Review Header */}
				<div className="flex items-start gap-3 mb-4">
					{/* Avatar */}
					<div className="w-12 h-12 rounded-full bg-gradient-to-br from-steerOrange to-orange-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
						{getInitials(review.author)}
					</div>

					{/* Author Info */}
					<div className="flex-1 min-w-0">
						<div className="flex items-center gap-2 flex-wrap">
							<h3 className="text-steerBlack font-semibold text-base truncate">
								{review.author}
							</h3>
							{review.isLocalGuide && (
								<span className="text-xs bg-blue-500/20 text-blue-600 px-2 py-0.5 rounded-full border border-blue-400/30 whitespace-nowrap">
									Local Guide
								</span>
							)}
							{review.isNew && (
								<span className="text-xs bg-green-500/20 text-green-600 px-2 py-0.5 rounded-full border border-green-400/30 whitespace-nowrap">
									New
								</span>
							)}
						</div>
						<p className="text-steerLightBlack text-xs">
							{review.reviewCount} reviews
							{review.photoCount > 0 && ` · ${review.photoCount} photos`}
						</p>
					</div>
				</div>

				{/* Rating and Date */}
				<div className="flex items-center justify-between mb-3">
					{renderStars(review.rating)}
					<span className="text-gray-500 text-xs">{review.date}</span>
				</div>

				{/* Review Text - Always truncated to 4 lines */}
				<div className="flex-grow">
					<p className="text-steerLightBlack leading-relaxed text-sm line-clamp-3">
						{review.text}
					</p>
				</div>

				{/* Reactions */}
				{/* {review.reactions && (
					<div className="mt-3 flex items-center gap-2">
						{review.reactions.heart && (
							<span className="text-xs text-gray-500">
								❤️ {review.reactions.heart}
							</span>
						)}
					</div>
				)} */}
			</div>
		);
	};

	return (
		<section className="w-full overflow-hidden">
			<div className="">
				{/* Header and Summary */}
				<div className="text-center mb-12 flex flex-col items-center">
					{/* Google Rating Summary */}
					<motion.div
						initial={{ opacity: 0, scale: 0.9 }}
						whileInView={{ opacity: 1, scale: 1 }}
						viewport={{ once: true }}
						transition={{ duration: 0.6, delay: 0.2 }}
						className="flex flex-wrap items-cente justify-center gap-6 bg-white rounded-2xl px-8 py-6 border border-gray-200 shadow-lg mt-8"
					>
						<img
							src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png"
							alt="Google"
							className="h-8"
						/>
						<div className="flex items-center gap-3">
							<span className="text-5xl font-bold text-steerBlack">
								{overallRating}
							</span>
							<div className="flex flex-col gap-1">
								{renderStars(5)}
								<p className="text-steerLightBlack text-sm">
									{totalReviews} reviews
								</p>
							</div>
						</div>
						<div className="hidden md:flex h-12 w-px bg-gray-300"></div>
						<div className="flex flex-col gap-2 text-left">
							<p className="text-steerBlack font-semibold text-sm">
								{businessName}
							</p>
							<a
								href="https://www.google.com/maps/place/Steer+Smart+Driving+School/data=!4m2!3m1!1s0x0:0x37095f6bf58158d4?sa=X&ved=1t:2428&ictx=111"
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex items-center gap-1 text-steerOrange hover:text-orange-600 transition-colors text-sm font-medium"
							>
								View all reviews on Google
								<svg
									className="w-3.5 h-3.5"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
									/>
								</svg>
							</a>
						</div>
					</motion.div>
				</div>

				{/* Swiper Carousel */}
				<div className="carousel-mask">
					<Swiper
						modules={[Navigation, Pagination, EffectCoverflow]}
						effect="coverflow"
						grabCursor={true}
						centeredSlides={true}
						slidesPerView="auto"
						spaceBetween={50}
						coverflowEffect={{
							rotate: 0,
							stretch: 0,
							depth: 200,
							modifier: 1.5,
							slideShadows: false,
						}}
						navigation={{
							nextEl: ".swiper-button-next-custom",
							prevEl: ".swiper-button-prev-custom",
						}}
						pagination={{
							el: ".swiper-pagination-custom",
							clickable: true,
						}}
						loop={true}
						observer={true}
						observeParents={true}
						className="reviews-swiper"
					>
						{reviews.map((review) => (
							<SwiperSlide key={review.id} className="!w-[90%] md:!w-[400px]">
								<ReviewCard review={review} />
							</SwiperSlide>
						))}
					</Swiper>
				</div>

				{/* Custom Navigation */}
				<div className="flex items-center justify-center gap-2 mb-8">
					<button
						className="swiper-button-prev-custom cursor-pointer w-10 h-10 rounded-full bg-steerOrange text-white flex items-center justify-center hover:bg-steerOrange transition-all duration-300 hover:scale-110 shadow-md"
						aria-label="Previous review"
					>
						<FaChevronLeft className="w-4 h-4" />
					</button>

					<div className="swiper-pagination-custom !w-auto flex items-center justify-center px-6"></div>

					<button
						className="swiper-button-next-custom cursor-pointer w-10 h-10 rounded-full bg-steerOrange text-white flex items-center justify-center hover:bg-steerOrange transition-all duration-300 hover:scale-110 shadow-md"
						aria-label="Next review"
					>
						<FaChevronRight className="w-4 h-4" />
					</button>
				</div>
			</div>

			{/* Custom Swiper Styles */}
			<style>{`
				.carousel-mask {
					mask-image: linear-gradient(to right, transparent, black 5%, black 95%, transparent);
					-webkit-mask-image: linear-gradient(to right, transparent, black 5%, black 95%, transparent);
				}

				.reviews-swiper {
					padding: 40px 0;
				}

				.swiper-pagination-custom :global(.swiper-pagination-bullet) {
					width: 10px;
					height: 10px;
					background: #d1d5db;
					opacity: 1;
					transition: all 0.3s;
					margin: 0 6px !important;
				}

				.swiper-pagination-custom :global(.swiper-pagination-bullet-active) {
					width: 24px;
					height: 10px;
					border-radius: 5px;
					background: #4f200d !important;
				}

				.swiper-slide {
					transition: all 0.3s;
				}

				.swiper-slide-active {
					transform: scale(1.05);
				}

				.swiper-slide:not(.swiper-slide-active) {
					transform: scale(0.9);
				}

				.swiper-slide:not(.swiper-slide-active) > div {
					opacity: 0.5;
				}
			`}</style>
		</section>
	);
};

export default GoogleReviews;
